import {Pressable, type StyleProp, View, type ViewStyle} from 'react-native'
import {type AppBskyFeedDefs, AppBskyFeedPost} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {useGetTimeAgo} from '#/lib/hooks/useTimeAgo'
import {sanitizeHandle} from '#/lib/strings/handles'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {usePostThreadPress} from '../util'

/**
 * Compact, non-interactive post preview shown inside trend cards.
 *
 * Mirrors Figma node `384:53777` in file `C8J3bA4ThNVCG0vzlHeI16`: a single
 * author row (avatar · name · handle · time) followed by the post text
 * truncated to two lines. Intentionally omits embeds and the reply/repost/like
 * controls — it's a teaser, not a full post. Tapping a trend card navigates to
 * the trend search where the full posts live.
 */
export function CompactPostPreview({
  post,
  style,
  truncate = true,
}: {
  post: AppBskyFeedDefs.PostView
  style?: StyleProp<ViewStyle>
  /** When false, show the full post text instead of capping at two lines. */
  truncate?: boolean
}) {
  const t = useTheme()
  const ss = useSemanticTokens()
  const {_} = useLingui()
  const getTimeAgo = useGetTimeAgo()
  const onPostPress = usePostThreadPress()

  const author = post.author
  const record = post.record
  const text =
    AppBskyFeedPost.isRecord(record) && typeof record.text === 'string'
      ? record.text
      : ''
  const name = author.displayName?.trim() || sanitizeHandle(author.handle)
  const timeAgo = getTimeAgo(post.indexedAt, Date.now())

  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={_(msg`Post by ${name}`)}
      accessibilityHint={_(msg`Opens the post`)}
      onPress={() => onPostPress(post)}
      style={[
        a.w_full,
        a.gap_sm,
        {padding: 20, borderRadius: 24, backgroundColor: ss.bg.contrast_25},
        style,
      ]}>
      <View style={[a.flex_row, a.align_center, a.gap_xs]}>
        <UserAvatar type="user" size={20} avatar={author.avatar} />
        <Text numberOfLines={1} style={[a.flex_1, a.text_sm, a.leading_snug]}>
          <Text style={[a.font_bold, t.atoms.text]}>{name}</Text>
          <Text style={[t.atoms.text_contrast_medium]}>
            {'  '}
            {sanitizeHandle(author.handle, '@')} · {timeAgo}
          </Text>
        </Text>
      </View>

      <Text
        numberOfLines={truncate ? 2 : undefined}
        style={[a.text_sm, a.leading_snug, t.atoms.text]}>
        {text}
      </Text>
    </Pressable>
  )
}
